import { useCallback } from 'react';

/**
 * Allowed event types for type safety
 */
type TrackableEvent =
  | 'upgrade_click'
  | 'manage_subscription_click'
  | 'portal_opened'
  | 'cancel_initiated';

/**
 * Hook for tracking user events
 *
 * Usage:
 * ```tsx
 * const { track } = useTrackEvent();
 *
 * const handleUpgrade = async () => {
 *   await track('upgrade_click');
 *   // ... rest of logic
 * };
 * ```
 */
export function useTrackEvent() {
  const track = useCallback(async (
    event: TrackableEvent,
    data?: Record<string, unknown>
  ): Promise<void> => {
    try {
      await fetch('/api/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ event, data }),
      });
    } catch (err) {
      // Silent fail - don't break UX for analytics
      console.debug('[Analytics] Event track failed:', err);
    }
  }, []);

  return { track };
}
